/**
 * The multipart upload client.
 *
 * Files go from the browser straight into S3, so the API never carries a byte of
 * a 500 MB video. What that costs is presigned URLs, and presigned URLs made by
 * a Lambda expire with the Lambda's own credentials — roughly fifteen minutes,
 * sometimes less. A store's upstream link can easily take longer than that to
 * move half a gigabyte.
 *
 * Two rules follow, and between them they are the difference between an upload
 * that finishes over a shop's DSL and one that does not:
 *
 *   1. Part URLs are fetched a few at a time, just ahead of where the upload has
 *      reached, rather than all at once at the start. A URL minted for part 20
 *      while part 3 is in flight has already expired by the time it is needed.
 *   2. **Any** 403 on a part PUT is read as "this URL has expired", not as a
 *      failure. The batch is discarded, fresh URLs are fetched, and the part is
 *      sent again. S3 answers an expired signature with 403 and there is nothing
 *      else a correctly signed PUT to a correct key can be refused for here, so
 *      treating the status as anything other than "mint a new one" turns a
 *      recoverable moment into a lost upload.
 *
 * Parts go one at a time. Concurrency would shorten a fast upload and lengthen a
 * congested one, and the connection this has to survive is the congested one.
 *
 * Everything network-facing sits behind `PartTransport`, so the retry, expiry
 * and cancellation logic can be driven by a fake in tests without a browser.
 */

import { UPLOAD_PART_URL_BATCH, UPLOAD_PART_SIZE } from '@pbtv/shared';
import type { AssetType, UploadInit } from '@pbtv/shared';
import type { MediaAsset, MediaScope, PartUrl } from './media.js';
import { completeUpload, fetchPartUrls, initiateUpload } from './media.js';
import type { MediaKind, MediaProbe } from './probe.js';
import { makeThumbnail, putThumbnail } from './thumbnail.js';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled.');
    this.name = 'UploadCancelledError';
  }
}

export class UploadError extends Error {
  readonly partNumber: number | null;
  readonly status: number | null;

  constructor(message: string, partNumber: number | null = null, status: number | null = null) {
    super(message);
    this.name = 'UploadError';
    this.partNumber = partNumber;
    this.status = status;
  }
}

export function isCancellation(value: unknown): boolean {
  if (value instanceof UploadCancelledError) return true;
  return value instanceof DOMException && value.name === 'AbortError';
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new UploadCancelledError();
}

// ---------------------------------------------------------------------------
// Part planning
// ---------------------------------------------------------------------------

export interface PartPlan {
  partNumber: number;
  start: number;
  end: number;
  bytes: number;
}

/**
 * S3 numbers parts from 1 and requires every part except the last to be the same
 * size and at least 5 MiB. `UPLOAD_PART_SIZE` is well past that, so the only
 * shape worth getting right is the short final part.
 */
export function planParts(totalBytes: number, partSize: number = UPLOAD_PART_SIZE): PartPlan[] {
  if (totalBytes <= 0 || partSize <= 0) return [];

  const parts: PartPlan[] = [];
  let start = 0;
  let partNumber = 1;

  while (start < totalBytes) {
    const end = Math.min(start + partSize, totalBytes);
    parts.push({ partNumber, start, end, bytes: end - start });
    start = end;
    partNumber += 1;
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export interface PutPartRequest {
  partNumber: number;
  url: string;
  body: Blob;
  signal: AbortSignal;
  /** Bytes of THIS part sent so far; resets if the part is retried. */
  onBytes: (sent: number) => void;
}

export interface PutPartResult {
  status: number;
  /** Quotes stripped; null when CORS did not expose the header. */
  eTag: string | null;
}

export interface PartTransport {
  fetchPartUrls(partNumbers: readonly number[], signal: AbortSignal): Promise<PartUrl[]>;
  /** Resolves for any HTTP answer; rejects only when no answer arrived at all. */
  putPart(request: PutPartRequest): Promise<PutPartResult>;
}

export function normaliseETag(raw: string | null): string | null {
  if (raw === null) return null;
  const trimmed = raw.trim().replace(/^W\//i, '');
  const unquoted = trimmed.replace(/^"(.*)"$/, '$1');
  return unquoted === '' ? null : unquoted;
}

/**
 * `fetch` cannot report how much of a request body has gone out, and a 25 MiB
 * part on a slow link is minutes of a progress bar that would otherwise sit
 * still. XMLHttpRequest still has `upload.onprogress`, which is the whole reason
 * it is here.
 */
function putPartWithProgress(request: PutPartRequest): Promise<PutPartResult> {
  return new Promise<PutPartResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = (): void => xhr.abort();

    xhr.open('PUT', request.url, true);
    xhr.upload.onprogress = (event) => request.onBytes(event.loaded);
    xhr.onload = () =>
      resolve({ status: xhr.status, eTag: normaliseETag(xhr.getResponseHeader('ETag')) });
    xhr.onerror = () => reject(new Error('The connection dropped while sending this part.'));
    xhr.ontimeout = () => reject(new Error('This part timed out on the way to storage.'));
    xhr.onabort = () => reject(new UploadCancelledError());
    xhr.onloadend = () => request.signal.removeEventListener('abort', abort);

    request.signal.addEventListener('abort', abort, { once: true });
    // No Content-Type: a slice of a File carries no type, so the browser sends
    // none, and a header S3 did not sign is a header that cannot break the
    // signature.
    xhr.send(request.body);
  });
}

export function browserPartTransport(assetId: string): PartTransport {
  return {
    fetchPartUrls: (partNumbers, signal) => fetchPartUrls(assetId, partNumbers, signal),
    putPart: putPartWithProgress,
  };
}

// ---------------------------------------------------------------------------
// The part loop
// ---------------------------------------------------------------------------

export type PartOutcome = 'done' | 'expired' | 'retry' | 'fatal';

export function classifyPartStatus(status: number): PartOutcome {
  if (status >= 200 && status < 300) return 'done';
  // The one that matters. See the note at the top of the file.
  if (status === 403) return 'expired';
  if (status === 408 || status === 429 || status >= 500) return 'retry';
  return 'fatal';
}

const RETRY_BASE_MS = 500;
const RETRY_CAP_MS = 8000;

export function retryDelayMs(attempt: number): number {
  return Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1));
}

export interface UploadedPart {
  partNumber: number;
  eTag: string | null;
}

export interface UploadPartsOptions {
  parts: readonly PartPlan[];
  sliceOf: (part: PartPlan) => Blob;
  transport: PartTransport;
  signal: AbortSignal;
  /** Total bytes confirmed sent, including the part currently in flight. */
  onProgress: (uploadedBytes: number) => void;
  batchSize?: number;
  maxAttempts?: number;
  maxUrlRefreshes?: number;
  wait?: (ms: number) => Promise<void>;
}

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_MAX_URL_REFRESHES = 3;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function uploadParts({
  parts,
  sliceOf,
  transport,
  signal,
  onProgress,
  batchSize = UPLOAD_PART_URL_BATCH,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  maxUrlRefreshes = DEFAULT_MAX_URL_REFRESHES,
  wait = sleep,
}: UploadPartsOptions): Promise<UploadedPart[]> {
  const urls = new Map<number, string>();
  const done: UploadedPart[] = [];
  let completedBytes = 0;

  /**
   * Fetching drops the whole batch rather than one entry: the batch was minted
   * in a single call and therefore expires as a unit, so a 403 on one member is
   * a statement about all of them.
   */
  const urlFor = async (index: number, partNumber: number, refresh: boolean): Promise<string> => {
    if (!refresh) {
      const cached = urls.get(partNumber);
      if (cached !== undefined) return cached;
    }

    const wanted = parts.slice(index, index + batchSize).map((part) => part.partNumber);
    for (const number of wanted) urls.delete(number);

    const fresh = await transport.fetchPartUrls(wanted, signal);
    for (const entry of fresh) urls.set(entry.partNumber, entry.url);

    const url = urls.get(partNumber);
    if (url === undefined) {
      throw new UploadError(`No upload URL came back for part ${partNumber}.`, partNumber);
    }
    return url;
  };

  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (part === undefined) continue;

    let attempts = 0;
    let refreshes = 0;
    let refreshNext = false;

    for (;;) {
      throwIfAborted(signal);

      const url = await urlFor(index, part.partNumber, refreshNext);
      refreshNext = false;

      let result: PutPartResult;
      try {
        result = await transport.putPart({
          partNumber: part.partNumber,
          url,
          body: sliceOf(part),
          signal,
          onBytes: (sent) => onProgress(completedBytes + Math.min(sent, part.bytes)),
        });
      } catch (cause) {
        if (isCancellation(cause)) throw new UploadCancelledError();
        throwIfAborted(signal);

        attempts += 1;
        if (attempts >= maxAttempts) {
          throw new UploadError(
            `Part ${part.partNumber} could not be sent after ${attempts} attempts. Check the connection and start the upload again.`,
            part.partNumber,
          );
        }
        await wait(retryDelayMs(attempts));
        continue;
      }

      const outcome = classifyPartStatus(result.status);

      if (outcome === 'done') {
        done.push({ partNumber: part.partNumber, eTag: result.eTag });
        completedBytes += part.bytes;
        onProgress(completedBytes);
        break;
      }

      if (outcome === 'expired') {
        refreshes += 1;
        if (refreshes > maxUrlRefreshes) {
          throw new UploadError(
            `Storage kept refusing part ${part.partNumber} even with a freshly issued URL. Start the upload again.`,
            part.partNumber,
            result.status,
          );
        }
        // No backoff: a newly minted URL works immediately, and waiting here
        // only burns more of the next one's lifetime.
        refreshNext = true;
        continue;
      }

      if (outcome === 'fatal') {
        throw new UploadError(
          `Storage rejected part ${part.partNumber} with status ${result.status}.`,
          part.partNumber,
          result.status,
        );
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        throw new UploadError(
          `Part ${part.partNumber} kept failing with status ${result.status}. Try the upload again in a few minutes.`,
          part.partNumber,
          result.status,
        );
      }
      await wait(retryDelayMs(attempts));
    }
  }

  return done;
}

// ---------------------------------------------------------------------------
// One file, end to end
// ---------------------------------------------------------------------------

export type UploadStage = 'starting' | 'thumbnail' | 'uploading' | 'finishing' | 'done';

export interface UploadProgress {
  stage: UploadStage;
  uploadedBytes: number;
  totalBytes: number;
  /** 0 to 1, for the bar. */
  fraction: number;
}

export interface UploadRequest {
  file: File;
  kind: MediaKind;
  contentType: AssetType;
  probe: MediaProbe;
  title: string;
  scope: MediaScope;
}

export interface UploadHooks {
  signal: AbortSignal;
  onProgress: (progress: UploadProgress) => void;
}

/** The asset, when the completion route returned one this build understands. */
export async function uploadMedia(
  request: UploadRequest,
  { signal, onProgress }: UploadHooks,
): Promise<MediaAsset | null> {
  const totalBytes = request.file.size;
  const report = (stage: UploadStage, uploadedBytes: number): void => {
    onProgress({
      stage,
      uploadedBytes,
      totalBytes,
      fraction: totalBytes === 0 ? 0 : Math.min(1, uploadedBytes / totalBytes),
    });
  };

  throwIfAborted(signal);
  report('starting', 0);

  const body: UploadInit = {
    fileName: request.file.name,
    contentType: request.contentType,
    bytes: totalBytes,
    title: request.title,
    scope: request.scope,
    probe: request.probe,
  };
  const started = await initiateUpload(body, signal);

  const parts = planParts(totalBytes, started.partSize);
  if (parts.length !== started.partCount) {
    throw new UploadError(
      'The server and the browser disagree about how this file splits into parts. Reload the panel and try again.',
    );
  }

  // Before the bytes rather than after: making the thumbnail needs a decode, and
  // a decode that is going to fail should fail in a second, not after half an
  // hour of upload. It is never fatal — the asset simply has no poster frame.
  throwIfAborted(signal);
  report('thumbnail', 0);
  const thumbnail = await makeThumbnail(request.file, request.kind);
  if (thumbnail !== null) await putThumbnail(started.thumbPutUrl, thumbnail, signal);

  throwIfAborted(signal);
  report('uploading', 0);
  const uploaded = await uploadParts({
    parts,
    sliceOf: (part) => request.file.slice(part.start, part.end),
    transport: browserPartTransport(started.assetId),
    signal,
    onProgress: (uploadedBytes) => report('uploading', uploadedBytes),
  });

  report('finishing', totalBytes);
  const complete: { partNumber: number; eTag: string }[] = [];
  for (const part of uploaded) {
    if (part.eTag !== null) complete.push({ partNumber: part.partNumber, eTag: part.eTag });
  }

  const asset = await completeUpload(
    started.assetId,
    complete.length === uploaded.length ? complete : null,
    signal,
  );

  report('done', totalBytes);
  return asset;
}
